import React from 'react';
import { MapPin, Navigation, Loader2, X, Crosshair } from 'lucide-react';
import { SelectionMode } from '../../types/fare';

interface FloatingLocationInputProps {
  pickupAddress: string;
  destinationAddress: string;
  onCalculate: () => void;
  onPickupClick: () => void;
  onDestinationClick: () => void;
  onCancelSelection?: () => void;
  onUseCurrentLocation?: () => void;
  loading: boolean;
  hasValidInputs: boolean;
  selectionMode: SelectionMode;
}

export default function FloatingLocationInput({
  pickupAddress,
  destinationAddress,
  onCalculate,
  onPickupClick,
  onDestinationClick,
  onCancelSelection,
  onUseCurrentLocation,
  loading,
  hasValidInputs,
  selectionMode,
}: FloatingLocationInputProps) {
  // Active pickup selection mode
  if (selectionMode === 'pickup') {
    return (
      <div className="absolute left-4 right-4 z-30 md:hidden" style={{ top: 'max(1rem, env(safe-area-inset-top, 1rem))', maxHeight: 'calc(100vh - 8rem - max(0px, env(safe-area-inset-top)) - max(0px, env(safe-area-inset-bottom)))' }}>
        <div className="bg-green-500 rounded-2xl shadow-xl p-4 transition-all duration-300 overflow-y-auto">
          <div className="flex items-center justify-between mb-2">
            <div className="text-white text-sm font-semibold">
              📍 Tap on the map to set pickup location
            </div>
            {onCancelSelection && (
              <button
                onClick={onCancelSelection}
                className="text-white hover:bg-green-600 rounded-full p-1 transition-colors"
              >
                <X size={20} />
              </button>
            )}
          </div>
          <button
            className="w-full flex items-center gap-3 p-3 bg-white rounded-lg text-left mb-3"
          >
            <div className="flex-shrink-0">
              <div className="w-3 h-3 rounded-full bg-green-500" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs text-gray-500 mb-0.5">Pickup</div>
              <div className="text-sm font-medium text-gray-900 truncate">
                {pickupAddress || 'Selecting...'}
              </div>
            </div>
            <MapPin size={18} className="text-green-500 flex-shrink-0" />
          </button>
          
          {/* Use Current Location Button */}
          {onUseCurrentLocation && (
            <button
              onClick={onUseCurrentLocation}
              className="w-full bg-white text-green-600 py-3 px-4 rounded-lg font-semibold hover:bg-green-50 transition-colors flex items-center justify-center gap-2"
            >
              <Crosshair size={18} />
              Use Current Location
            </button>
          )}
        </div>
      </div>
    );
  }

  // Active destination selection mode
  if (selectionMode === 'destination') {
    return (
      <div className="absolute left-4 right-4 z-30 md:hidden" style={{ top: 'max(1rem, env(safe-area-inset-top, 1rem))', maxHeight: 'calc(100vh - 8rem - max(0px, env(safe-area-inset-top)) - max(0px, env(safe-area-inset-bottom)))' }}>
        <div className="bg-red-500 rounded-2xl shadow-xl p-4 transition-all duration-300 overflow-y-auto">
          <div className="flex items-center justify-between mb-2">
            <div className="text-white text-sm font-semibold">
              🎯 Tap on the map to set destination
            </div>
            {onCancelSelection && (
              <button
                onClick={onCancelSelection}
                className="text-white hover:bg-red-600 rounded-full p-1 transition-colors"
              >
                <X size={20} />
              </button>
            )}
          </div>
          <button
            className="w-full flex items-center gap-3 p-3 bg-white rounded-lg text-left"
          >
            <div className="flex-shrink-0">
              <div className="w-3 h-3 rounded-full bg-red-500" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs text-gray-500 mb-0.5">Destination</div>
              <div className="text-sm font-medium text-gray-900 truncate">
                {destinationAddress || 'Selecting...'}
              </div>
            </div>
            <Navigation size={18} className="text-red-500 flex-shrink-0" />
          </button>
        </div>
      </div>
    );
  }

  // Normal mode - show all fields
  return (
    <div className="absolute left-4 right-4 z-30 md:hidden" style={{ top: 'max(1rem, env(safe-area-inset-top, 1rem))', maxHeight: 'calc(100vh - 8rem - max(0px, env(safe-area-inset-top)) - max(0px, env(safe-area-inset-bottom)))' }}>
      <div className="bg-white rounded-2xl shadow-xl p-4 transition-all duration-300 overflow-y-auto">
        {/* Pickup */}
        <button
          onClick={onPickupClick}
          className="w-full flex items-center gap-3 p-3 bg-gray-50 rounded-lg mb-2 text-left hover:bg-gray-100 transition-colors"
        >
          <div className="flex-shrink-0">
            <div className="w-3 h-3 rounded-full bg-green-500" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs text-gray-500 mb-0.5">Pickup</div>
            <div className="text-sm font-medium text-gray-900 truncate">
              {pickupAddress || 'Tap to select on map'}
            </div>
          </div>
          <MapPin size={18} className="text-gray-400 flex-shrink-0" />
        </button>

        {/* Destination */}
        <button
          onClick={onDestinationClick}
          className="w-full flex items-center gap-3 p-3 bg-gray-50 rounded-lg mb-3 text-left hover:bg-gray-100 transition-colors"
        >
          <div className="flex-shrink-0">
            <div className="w-3 h-3 rounded-full bg-red-500" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs text-gray-500 mb-0.5">Destination</div>
            <div className="text-sm font-medium text-gray-900 truncate">
              {destinationAddress || 'Tap to select on map'}
            </div>
          </div>
          <Navigation size={18} className="text-gray-400 flex-shrink-0" />
        </button>

        {/* Calculate Button */}
        <button
          onClick={onCalculate}
          disabled={loading || !hasValidInputs}
          className="w-full bg-green-500 text-white py-3 px-4 rounded-lg font-semibold hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Calculating...
            </>
          ) : (
            'Calculate Fare'
          )}
        </button>
      </div>
    </div>
  );
}
